const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Max width caps per size context so an extreme landscape logo can never
// break card layouts. Height is admin-controlled per logo (display_height).
const MAX_LOGO_WIDTH = { sm: 100, md: 150, lg: 220 };
// If a brand has no logo, we fall back to the navy short-code square at
// these fixed sizes.
const FALLBACK_SIZE_CLASS = {
    sm: "brand-mark-sm",
    md: "brand-mark-md",
    lg: "brand-mark-lg",
};

/**
 * BrandMark
 * - When `brand.active_logo` is present, renders the uploaded logo image at the
 *   admin-configured display height with width:auto and a size-aware max-width.
 * - Otherwise falls back to a navy square displaying the brand short code.
 * - `code` prop is a plain string fallback for non-brand marks (e.g. the app's
 *   own top-bar mark).
 */
export function BrandMark({ brand, code, size = "md", className = "" }) {
    if (brand && brand.active_logo) {
        const logo = brand.active_logo;
        const src = `${BACKEND_URL}${logo.url}`;
        // Cap sm-context height so it doesn't tower over compact rows.
        const height =
            size === "sm"
                ? Math.min(logo.display_height, 24)
                : size === "lg"
                ? Math.max(logo.display_height, 32)
                : logo.display_height;
        return (
            <img
                src={src}
                alt={brand.label || brand.short_code || "Brand logo"}
                data-testid={`brand-logo-${brand.key}`}
                className={className}
                style={{
                    height,
                    width: "auto",
                    maxWidth: MAX_LOGO_WIDTH[size] || MAX_LOGO_WIDTH.md,
                    objectFit: "contain",
                    display: "inline-block",
                    verticalAlign: "middle",
                }}
            />
        );
    }

    const shortCode = code || brand?.short_code || "??";
    return (
        <span
            className={`brand-mark ${FALLBACK_SIZE_CLASS[size] || FALLBACK_SIZE_CLASS.md} ${className}`}
            aria-label={shortCode}
        >
            {shortCode}
        </span>
    );
}

export function Avatar({ initials, name, size = "md" }) {
    const cls = size === "sm" ? "avatar-sm" : "avatar-md";
    return (
        <span className={`avatar ${cls}`} title={name}>
            {initials || (name ? name.slice(0, 1).toUpperCase() : "?")}
        </span>
    );
}
